import { useCallback, useMemo, type ReactNode, type RefCallback, type RefObject } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { functionWithFG } from '@atlaskit/platform-feature-flags-react';

import type { ItemDragAndDropGroup, NavItemConfigWithCustomizationOptions } from '../../types';
import {
	createL1ItemData,
	getL1ItemData,
	type IsItemDraggable,
	type IsItemDroppable,
	isL1ItemData,
	isL1ItemDataInGroup,
	type TL1ItemData,
} from '../dnd-data';

import { useItemDragAndDrop } from './index';

interface L1ItemDragAndDrop {
	draggableRef: RefCallback<HTMLAnchorElement | HTMLButtonElement> | undefined;
	dropIndicator: ReactNode | undefined;
	dropTargetRef: RefObject<HTMLDivElement> | undefined;
	isDragging: boolean;
	isItemDraggable: boolean;
	isItemDroppable: boolean;
	l1DragAndDropEnabled?: boolean;
	maybeDragPreview: ReactNode;
}

interface UseL1ItemDragAndDropProps {
	group: ItemDragAndDropGroup;
	id: string;
	isExpandable: boolean;
	isExpandOrFlyoutOpen: boolean;
	item: NavItemConfigWithCustomizationOptions;
	l1DragAndDropEnabled?: boolean;
	onExpandFlyoutToggle: (open: boolean) => void;
	peek: boolean;
}

export const useL1ItemDragAndDrop = functionWithFG(
	'navx-4339-fix-gsn-jira-ref-stability',
	useL1ItemDragAndDropNEW,
	useL1ItemDragAndDropOLD,
);

function useL1ItemDragAndDropNEW({
	item,
	id,
	isExpandable,
	isExpandOrFlyoutOpen,
	onExpandFlyoutToggle,
	peek,
	l1DragAndDropEnabled: l1DragAndDropEnabledProp,
	group,
}: UseL1ItemDragAndDropProps): L1ItemDragAndDrop {
	const l1DragAndDropEnabled = l1DragAndDropEnabledProp && !fg('navx-gsn-dnc-disable-dnd');
	const data = useMemo(
		() =>
			createL1ItemData({
				menuItemId: id,
				group,
				isHidden: false,
				isHideable: item.isHideable !== false,
				label: item.label,
				isReorderable: item.isReorderable !== false,
			}),
		[group, id, item.isHideable, item.isReorderable, item.label],
	);

	const isItemDraggable: IsItemDraggable<TL1ItemData> = useCallback(() => {
		return (
			!!l1DragAndDropEnabled &&
			!peek &&
			(item.isHideable !== false || item.isReorderable !== false) &&
			(item.type !== 'custom' || !!item.isDraggable)
		);
	}, [
		item.isDraggable,
		item.isHideable,
		item.isReorderable,
		item.type,
		l1DragAndDropEnabled,
		peek,
	]);

	const isItemDroppable: IsItemDroppable<TL1ItemData> = useCallback(
		() => !!l1DragAndDropEnabled && !peek && (item.type !== 'custom' || !!item.isDraggable),
		[item.isDraggable, item.type, l1DragAndDropEnabled, peek],
	);

	return useItemDragAndDrop<TL1ItemData>({
		data,
		getItemData: getL1ItemData,
		group,
		id,
		item,
		isDragAndDropEnabled: l1DragAndDropEnabled,
		isExpandable,
		isExpandOrFlyoutOpen,
		isItemData: isL1ItemData,
		isItemDataInGroup: isL1ItemDataInGroup,
		isItemDraggable,
		isItemDroppable,
		onExpandFlyoutToggle,
	});
}

function useL1ItemDragAndDropOLD({
	item,
	id,
	isExpandable,
	isExpandOrFlyoutOpen,
	onExpandFlyoutToggle,
	peek,
	l1DragAndDropEnabled: l1DragAndDropEnabledProp,
	group,
}: UseL1ItemDragAndDropProps): L1ItemDragAndDrop {
	const l1DragAndDropEnabled = l1DragAndDropEnabledProp && !fg('navx-gsn-dnc-disable-dnd');
	const baseData = {
		menuItemId: id,
		group,
		isHidden: false,
		isHideable: item.isHideable !== false,
		label: item.label,
		isReorderable: item.isReorderable !== false,
	};
	const data = createL1ItemData(baseData);
	const isItemDraggable: IsItemDraggable<TL1ItemData> = () => {
		return (
			!!l1DragAndDropEnabled &&
			!peek &&
			(item.isHideable !== false || item.isReorderable !== false) &&
			(item.type !== 'custom' || !!item.isDraggable)
		);
	};

	const isItemDroppable: IsItemDroppable<TL1ItemData> = () =>
		!!l1DragAndDropEnabled && !peek && (item.type !== 'custom' || !!item.isDraggable);

	return useItemDragAndDrop<TL1ItemData>({
		data,
		getItemData: getL1ItemData,
		group,
		id,
		item,
		isDragAndDropEnabled: l1DragAndDropEnabled,
		isExpandable,
		isExpandOrFlyoutOpen,
		isItemData: isL1ItemData,
		isItemDataInGroup: isL1ItemDataInGroup,
		isItemDraggable,
		isItemDroppable,
		onExpandFlyoutToggle,
	});
}
